
import React from "react";
import { i18nManager } from "../../../../../i18n";
import { Elements } from "../../../../shared/Elements";
import { ITournamentEditionMatch, ITournamentEditionRegisteredParticipant, TournamentMatchEndScore, TournamentMatchStatus } from "../../../../../services";
import PageDesign from "../../../../page_design/PageDesign";
import { Button, DOMHelper } from "@rivtn/noseur";
import { ConfirmDialog } from "../../../../shared/ConfirmDialog";
import { Schemes, Utils } from "../../../../../utils";

export const matchStatusColorMap = {
    [TournamentMatchStatus.ENDED]: "#1D09D3",
    [TournamentMatchStatus.ONGOING]: "#09B1D3",
    [TournamentMatchStatus.UPCOMING]: "#D39A09",
    [TournamentMatchStatus.FORFEITED]: "#2b2929",
    [TournamentMatchStatus.CANCELLED]: "#98999A",
};
export const endedMatchColorMap = {
    [TournamentMatchEndScore.WON]: "rgb(15, 168, 131)",
    [TournamentMatchEndScore.FORFEITED]: "rgb(0, 0, 0)",
    [TournamentMatchEndScore.DRAW]: "rgb(255, 210, 113)",
    [TournamentMatchEndScore.LOST]: "rgb(255, 113, 113)",
    [TournamentMatchEndScore.ABSENT]: "rgb(228, 33, 189)",
};
export const endedMatchSecondaryColorMap = {
    [TournamentMatchEndScore.WON]: "rgba(15, 168, 131, 0.2)",
    [TournamentMatchEndScore.FORFEITED]: "rgba(0, 0, 0, 0.2)",
    [TournamentMatchEndScore.DRAW]: "rgba(255, 210, 113, 0.2)",
    [TournamentMatchEndScore.LOST]: "rgba(255, 113, 113, 0.2)",
    [TournamentMatchEndScore.ABSENT]: "rgba(228, 33, 189, 0.2)",
};

export interface MatchCardProps {
    tournamentName: string;
    match: ITournamentEditionMatch;
    tournamentEditionRegisteredParticipantId: string;
    onAction: (match: ITournamentEditionMatch, action?: string) => void;
}

function MatchCard(props: MatchCardProps) {
    const { match, tournamentName, tournamentEditionRegisteredParticipantId } = props;
    const labels = i18nManager.Labels.tournaments.your_tournament.matches_and_reporting;
    const {
        me, opponents, opponentsScore, thereIsNoOpponent, isVersus, isMultiVersus, ended, ongoing, endedScoreLabel, statusLabel, myDisplay, opponentsDisplay
    } = computeMatchVariables(match, tournamentEditionRegisteredParticipantId);

    return (<div key={DOMHelper.uniqueElementId("mcmatch")} className="match" style={ended ? {
        borderColor: endedMatchColorMap[match.score ?? TournamentMatchEndScore.LOST]
    } : undefined} onClick={() => {
        props.onAction(match);
    }}>
        <div className="ma-container">
            <div className="fainter" style={thereIsNoOpponent ? { justifyContent: "center" } : undefined}>
                {(me as any).__isHomeMatch
                    ? (<>{myDisplay} {(isMultiVersus) ? <div /> : opponentsDisplay}</>)
                    : (<>{(isMultiVersus) ? <div /> : opponentsDisplay} {myDisplay}</>)}
            </div>
            <div className="snapper" style={{ justifyContent: "space-between" }}>
                <span style={{ color: matchStatusColorMap[match.status], fontWeight: "bold", }}>{statusLabel} {endedScoreLabel}</span>
                {Elements.buildSnapper((e) => {
                    e.stopPropagation();
                    let type: "versus" | "multi_versus" | "solo" = "solo";
                    if (isVersus) type = "versus";
                    else if (isMultiVersus) type = "multi_versus";

                    onScreenshot(match, tournamentName, type, opponents, labels);
                })}
            </div>
            <span className="litem" style={{ opacity: 0.6 }}>
                {labels.group} {match.group_number} - {labels.match.match} {match.match_number}
            </span>
            <span className="litem">
                {Utils.formatToFineDate2(match.start_date)}
            </span>
            {/* on hover on each team show name */}
            <div className="versus" style={thereIsNoOpponent ? { justifyContent: "center" } : undefined}>
                {(me as any).__isHomeMatch
                    ? (<>
                        <div className="comp">
                            {myDisplay}
                            {/* {(ended) && (<span className="s">{me.scores?.[match.determining_score]}{labels.score_labels[match.scores[match.determining_score]]}</span>)} */}
                        </div>
                        {(isVersus || isMultiVersus) && (<>
                            <span className="vs">{ended ? "-" : labels.vs}</span>
                            <div className="comp">
                                {/* {(ended) && (<span className="s">{opponentsScore}{labels.score_labels[match.scores[match.determining_score]]}</span>)} */}
                                {opponentsDisplay}
                            </div>
                        </>)}
                    </>)
                    : (<>
                        {(isVersus || isMultiVersus) && (<>
                            <div className="comp">
                                {opponentsDisplay}
                                {/* {(ended) && (<span className="s">{opponentsScore}{labels.score_labels[match.scores[match.determining_score]]}</span>)} */}
                            </div>
                            <span className="vs">{ended ? "-" : labels.vs}</span>
                        </>)}
                        <div className="comp">
                            {/* {(ended) && (<span className="s">{me.scores?.[match.determining_score]}{labels.score_labels[match.scores[match.determining_score]]}</span>)} */}
                            {myDisplay}
                        </div>
                    </>)}
            </div>
        </div>
        {(match.location || (ongoing || ended))
            && (<div className="location" style={ongoing
                ? { padding: "10px 20px" }
                : (ended
                    ? { backgroundColor: endedMatchSecondaryColorMap[match.score ?? TournamentMatchEndScore.LOST] }
                    : undefined)}>
                {(match.location) ? (<span>
                    <span style={{ opacity: 0.5 }}>{labels.location}</span>{" "}
                    {match.location}
                </span>) : <div />}
                {(ongoing)
                    && (<Button raised scheme={Schemes.OMNEHOST} text={labels.submit_result} style={{ padding: "10px 25px" }} onClick={(e) => {
                        e.stopPropagation();
                        props.onAction(match, "sr");
                    }} />)}
                {(ended)
                    && (<Button raised scheme={Schemes.OMNEHOST} text={labels.view_result} style={{ padding: "10px 25px" }} onClick={(e) => {
                        e.stopPropagation();
                        props.onAction(match, "vr");
                    }} />)}
            </div>)}
    </div>);

}

export function computeMatchVariables(match: ITournamentEditionMatch, tournamentEditionRegisteredParticipantId: string) {
    const { me, opponents } = match.tournament_edition_registered_participants.reduce((acc, participant) => {
        if (participant.external_id === tournamentEditionRegisteredParticipantId) {
            acc.me = {
                ...participant,
                __isHomeMatch: !acc.opponents.length,
            };
        } else {
            acc.opponents.push(participant);
        }
        return acc;
    }, { opponents: [] } as any) as { me: ITournamentEditionRegisteredParticipant; opponents: ITournamentEditionRegisteredParticipant[]; };
    const opponentsScore = opponents.reduce((acc, ops) => {
        return acc + 0;//(ops.scores?.[match.determining_score] ?? 0);
    }, 0);
    const thereIsNoOpponent = opponents.length === 0;
    const ended = match.status === TournamentMatchStatus.ENDED;
    const ongoing = match.status === TournamentMatchStatus.ONGOING;
    const upcoming = match.status === TournamentMatchStatus.UPCOMING;
    const isVersus = match.tournament_edition_registered_participants.length === 2;
    const isMultiVersus = match.tournament_edition_registered_participants.length > 2;
    const statusLabel = (i18nManager.Labels.host.settings.manage_tournaments.statuses_labels as any)[match.status.toLocaleLowerCase()] ?? "Unknown";
    const endedScoreLabel = i18nManager.Labels.tournaments.your_tournament.matches_and_reporting.ended_match_score_labels?.[match.score?.toLocaleLowerCase() ?? match.status.toLowerCase()] ?? "";

    const myDisplay = (<img alt={Utils.getResourceName(me.resource)} src={Utils.getResourceImage(me.resource)} />);
    const opponentsDisplay = ((isVersus || isMultiVersus)
        ? ((isVersus)
            ? (<img alt={Utils.getResourceName(opponents[0].resource)} src={Utils.getResourceImage(opponents[0].resource)} />)
            : (<div className="pas">
                {opponents.slice(0, 5).map((opponent) => (<img key={Utils.getResourceName(opponent.resource)} alt={Utils.getResourceName(opponent.resource)} src={Utils.getResourceImage(opponent.resource)} />))}
                {(opponents.length > 5) && (<div className="c">+{opponents.length - 5}</div>)}
            </div>))
        : undefined);

    return {
        me, opponents, opponentsScore, thereIsNoOpponent, isVersus, isMultiVersus, ended, ongoing, upcoming, endedScoreLabel, statusLabel, myDisplay, opponentsDisplay
    };
}

export function onScreenshot(match: ITournamentEditionMatch, tournamentName: string, type: "versus" | "multi_versus" | "solo", opponents: ITournamentEditionRegisteredParticipant[], labels: any) {
    if (type === "versus") {
        showTournamentPass(tournamentName, {
            ...labels.designs_headers.versus_match,
            desc: labels.designs_headers.versus_match.desc.replaceAll("${opponent_name}", Utils.getResourceName(opponents[0].resource))
                .replaceAll("${type}", (labels.designs_headers as any)[match.status.toLowerCase()]),
        });
    } else if (type === "multi_versus") {
        showTournamentPass(tournamentName, {
            ...labels.designs_headers.multi_versus_match,
            desc: labels.designs_headers.multi_versus_match.desc.replaceAll("${count}", "" + opponents.length)
                .replaceAll("${type}", (labels.designs_headers as any)[match.status.toLowerCase()]),
        });
    } else {
        showTournamentPass(tournamentName, {
            ...labels.designs_headers.solo_match,
            desc: labels.designs_headers.solo_match.desc
                .replaceAll("${type}", (labels.designs_headers as any)[match.status.toLowerCase()]),
        });
    }
}

export function showTournamentPass(tournamentName: string, header: { title: string; desc: string }, passId?: string) {
    header.desc = header.desc.replace("${tour_name}", tournamentName);
    const dialog = ConfirmDialog({
        noControl: true,
        style: { maxWidth: "90%" },
        contentStyle: { padding: 0 },
        content: (<div className="tournament-pass">
            <PageDesign noControl header={header} leftPanelStyle={{ flex: "unset", maxHeight: 600 }} publicId={""} designType={""} onUpdate={updateDesign}
                secondaryControl={<div className="tour-diag-pass-share">
                    {Elements.buildShareToView()}
                    <span className="link" style={{ marginTop: 50 }} onClick={() => dialog?.hide()}>{i18nManager.Labels.common.close}</span>
                </div>}
                secondaryView={<div className="tour-diag-pass-cals">
                    <div />
                    <Button raised scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.download} style={{ margin: "20px 0px 0px 0px", padding: "10px 25px" }} />
                </div>} />
        </div>),
    });
    dialog.show();

    function updateDesign(pageDesign: any, onDone?: () => void) {

    }
}

export default MatchCard;
